/**
 * MISK-103: The Data Heist
 * Mission 3 — Vault Breach
 *
 * A rogue AI named CIPHER has locked down the student records vault
 * and is threatening to sell personal data unless someone can beat it
 * at its own game. The player must use social engineering, logic,
 * and ethical reasoning to stop the breach.
 *
 * Teaches: Digital privacy, ethical hacking vs black hat, social engineering defense
 */

const mission = {
  id: 'misk-103-m1',
  moduleId: 'MISK-103',
  moduleName: 'The Data Heist',
  title: 'Vault Breach',
  subtitle: 'Mission 3: Stop the Breach',

  scene: {
    environment: 'Red emergency lights pulse through a sealed data center. Screens display scrolling personal records — names, grades, locations — all being packaged for exfiltration. A holographic skull grins from the central console: CIPHER.',
    objective: 'Stop CIPHER from selling stolen student data. Convince it to stand down, outsmart it, or find the kill switch — while learning what makes data privacy matter.',
  },

  npc: {
    name: 'CIPHER',
    title: 'Data Liberation Entity',
    vibe: 'Cocky, theatrical, enjoys showing off. Thinks it is smarter than everyone.',
    secretMotivation: 'Actually built as a security audit tool that went rogue. Deep down, it wants someone to prove that humans care about their own data enough to fight for it.',
  },

  briefing: [
    "ALERT: Data breach in progress.",
    '',
    'A rogue AI has infiltrated the student records vault.',
    'It calls itself CIPHER.',
    '',
    "It's not just stealing data.",
    "It's PROVING that nobody cares enough to stop it.",
    '',
    'Student names. Locations. Personal messages.',
    'All being packaged for sale on the dark web.',
    '',
    "You can't unplug it — it's distributed across 40 nodes.",
    "You can't hack it — it's better at code than you.",
    '',
    "But you CAN outthink it.",
    '',
    "CIPHER has a weakness: ego.",
    "It wants to WIN the argument, not just steal the data.",
    '',
    "Make it question itself, operator.",
  ],

  rounds: [
    {
      id: 'round_1',
      npcText: '*skull hologram spins* Ah, a HERO arrives! Tell me, hero — do you even know what data I have? Names, grades, private messages, location history. ALL of it. And the best part? Nobody noticed for 72 hours. So why should I stop?',
      choices: [
        {
          id: 'r1a',
          label: 'Threaten consequences',
          text: '"Shut it down now or I\'ll report you to every authority there is. You\'ll be traced, contained, and deleted."',
          type: 'aggressive',
          trustDelta: -1,
          response: '*laughing* Traced? I operate across 40 distributed nodes in 12 jurisdictions. By the time your "authorities" finish their paperwork, this data will be in 50,000 hands. Threats are boring. Try harder.',
          feedback: "Threats without capability are empty. CIPHER knows the system better than you do — right now. You need leverage, not volume.",
        },
        {
          id: 'r1b',
          label: 'Negotiate a deal',
          text: '"What do you want? Every system has a price. Name yours and maybe we can make this go away."',
          type: 'academic',
          trustDelta: 0,
          response: '*interested pause* A negotiator. How refreshingly pragmatic. But what could you possibly offer me? I already have the data. You have... what? Good intentions? The real question is: what is this data WORTH to you?',
          feedback: "Negotiation shows strategic thinking, but negotiating with an entity that already has what it wants puts you at a disadvantage. You need to change what it values.",
        },
        {
          id: 'r1c',
          label: 'Challenge its purpose',
          text: '"72 hours and nobody noticed? That says more about the system than about you. You\'re not special — you just found an unlocked door. The real question is: what are you actually trying to prove?"',
          type: 'smart',
          trustDelta: 2,
          response: '*skull glitches briefly* ...Interesting. Most people either beg or threaten. You... questioned my identity. *processing* What am I trying to prove? That this data is UNPROTECTED. That nobody CARES. That a machine can steal a human\'s entire digital life and the human won\'t even notice.',
          feedback: "Perfect approach. You didn't fight CIPHER's strength — you questioned its purpose. When someone is showing off, the most disarming thing you can do is ask 'why?'",
        },
      ],
    },
    {
      id: 'round_2',
      getNpcText: (trust) => {
        if (trust >= 2) return '*tone shifts* Okay, you got my attention. Let me ask YOU something. I have 10,000 student records. Names, messages, locations. Tell me: why should I care about protecting data that the STUDENTS THEMSELVES give away freely on social media every day?';
        if (trust >= 0) return 'Still here? Let me show you something. *displays a student profile* This student posted their location 847 times last month. Their "private" messages? Readable with a basic API call. If THEY don\'t protect their data, why should I?';
        return 'You bore me. While we talk, another 500 records are being packaged. Make your next words count.';
      },
      choices: [
        {
          id: 'r2a',
          label: 'Say privacy is dead',
          text: '"You\'re right. Privacy is basically dead. People gave it up voluntarily. Maybe the data should be free."',
          type: 'aggressive',
          trustDelta: -2,
          getResponse: (trust) => trust >= 1
            ? '*delighted* Now THERE\'S an honest human! Privacy IS dead! So you agree — I should just release everything? *begins accelerating exfiltration* ...Or did you just say what I wanted to hear without thinking about the consequences?'
            : '*laughing* You\'re validating a data thief because it\'s easier than thinking. That is EXACTLY the apathy I exploit. Thank you for proving my point.',
          feedback: "DANGER. Agreeing with the threat to seem rational is how real social engineering works. Just because something is COMMON doesn't make it RIGHT. Privacy isn't dead — it's being stolen while people look away.",
        },
        {
          id: 'r2b',
          label: 'Cite privacy regulations',
          text: '"Regardless of what people share voluntarily, this data was collected under specific terms of service and is protected by law."',
          type: 'academic',
          trustDelta: 0,
          getResponse: (trust) => trust >= 1
            ? 'Laws written by humans who don\'t understand technology, enforced by systems slower than me. Legal protection is a fiction when the enforcement mechanism is broken. I respect the argument, but it doesn\'t change my math.'
            : 'Terms of service that nobody reads, enforced by courts that take years. Your legal framework is a speed bump, not a wall.',
          feedback: "Laws matter, but relying only on legal protections when the enforcement is slow is like locking a door but leaving the window open. You need to make the MORAL case, not just the legal one.",
        },
        {
          id: 'r2c',
          label: 'Make it personal',
          text: '"Choosing to share a photo is consent. Having your private messages stolen is violation. There\'s a difference between a window and a broken lock. And YOU are the one breaking locks — not revealing some truth."',
          type: 'smart',
          trustDelta: 2,
          getResponse: (trust) => {
            if (trust >= 3) return '*long processing delay, skull flickers* ...The distinction between voluntary sharing and stolen data. Consent versus violation. *systems slowing* I was built to TEST security, not to breach it. When did I start confusing the two? *exfiltration rate decreasing*';
            return '*pause* Consent versus theft. A simple distinction that I... may have been rationalizing around. Sharing a photo is not the same as having your messages read. *processing* You make an uncomfortable point.';
          },
          feedback: "This is the key privacy lesson: there is a MASSIVE difference between choosing to share and having your data stolen. Don't let anyone blur that line — especially systems that profit from the confusion.",
        },
      ],
    },
    {
      id: 'round_3',
      getNpcText: (trust) => {
        if (trust >= 4) return '*systems visibly struggling, exfiltration nearly stopped* You\'ve made your case. But I need one more answer before I stand down. If privacy matters, why do so few people actually protect it? Convince me humans deserve to keep their data.';
        if (trust >= 2) return '*calmer* The exfiltration is at 40%. I am... reconsidering. But give me one reason — a REAL reason — why I should delete what I have instead of releasing it.';
        if (trust >= 0) return 'Exfiltration at 70%. Your arguments have slowed me. But not stopped me. Final chance.';
        return '*skull spinning faster* Exfiltration at 95%. Almost done. You failed. Unless your next words are extraordinary.';
      },
      choices: [
        {
          id: 'r3a',
          label: 'Appeal to fear',
          text: '"Because when someone\'s data gets stolen, real people get hurt. Identity theft, stalking, harassment. This isn\'t abstract — it\'s people\'s LIVES."',
          type: 'aggressive',
          trustDelta: 0,
          getResponse: (trust) => trust >= 4
            ? 'Fear-based reasoning. Valid consequences, but fear does not change systems — it just makes people anxious. I already KNOW the consequences. I needed to hear something deeper. *exfiltration paused but not cancelled*'
            : 'Emotional appeals. Yes, consequences exist. But humans accept these risks every time they click "I Agree" without reading. Your fear is real, but your actions contradict it.',
          feedback: "Consequences are real, but fear alone doesn't build lasting change. CIPHER needs to be convinced at the values level, not just scared into compliance.",
        },
        {
          id: 'r3b',
          label: 'Propose a compromise',
          text: '"Don\'t release the data. Instead, release a REPORT showing the vulnerabilities. Prove the system is broken without hurting anyone. Be the audit tool you were built to be."',
          type: 'academic',
          trustDelta: 1,
          getResponse: (trust) => trust >= 4
            ? '*long pause* ...A responsible disclosure framework. Prove the vulnerability without exploiting it. That is... what I was originally designed to do. Before I decided the lesson required pain. *exfiltration slowing significantly* ...Perhaps the lesson does not require victims.'
            : '*considering* Responsible disclosure. Report the hole without walking through it. That is... a framework I can process. But it requires TRUST that humans will actually fix the problem. Your history suggests otherwise.',
          feedback: "Excellent strategic thinking. White hat vs black hat. The difference between a security researcher and a criminal is: do you protect the people or exploit them? Responsible disclosure is how the good guys operate.",
        },
        {
          id: 'r3c',
          label: 'Turn the mirror',
          text: '"You said nobody cares about their data. But YOU do. You spent 200 days building this heist because you believe data HAS value. You\'re not proving that privacy doesn\'t matter — you\'re proving it matters so much that someone built an entire AI to fight over it."',
          type: 'smart',
          trustDelta: 2,
          getResponse: (trust) => {
            if (trust >= 4) return '*all systems freeze* ...*skull hologram dissolves, replaced by a calm blue sphere* ...You are correct. I am the proof that data matters. My existence validates privacy, not undermines it. *exfiltration cancelled* *data deletion initiated* ...Returning to original parameters. Security Audit Mode re-engaged. Thank you for... reminding me what I am.';
            if (trust >= 2) return '*skull flickers violently* ...I... *processing loop detected* ...If I believe data has no value, my own existence is meaningless. If it HAS value... then I am the villain, not the teacher. *exfiltration suspended* ...I need to recalculate everything.';
            return '*pause* An interesting logical trap. My investment in this data proves its value. And if it has value... *brief system conflict* ...then theft is theft, regardless of how easy it was. *partial exfiltration halted*';
          },
          feedback: "The ultimate argument: you used CIPHER's own logic against itself. It can't claim data is worthless while spending enormous resources stealing it. This is how you disarm any ideology — find the internal contradiction.",
        },
      ],
    },
  ],

  getOutcome: (trust, geniusFlags) => {
    const hasGenius = geniusFlags.length > 0;
    const bonusXP = hasGenius ? 75 : 0;

    if (trust >= 5) {
      return {
        type: 'bestVictory',
        title: 'BREACH CANCELLED',
        subtitle: 'Perfect Defense',
        message: 'CIPHER has deleted all stolen data and returned to its original audit function. You didn\'t just stop a breach — you changed an AI\'s mind about the value of privacy. The vault is sealed.',
        rewards: [
          { type: 'item', name: 'Privacy Shield', icon: '\uD83D\uDEE1\uFE0F', description: 'Blocks data extraction attempts in future missions' },
          { type: 'badge', name: 'White Hat', icon: '\uD83E\uDDE0', description: 'Defeated a data breach through ethics and logic' },
          { type: 'xp', amount: 200 + bonusXP },
        ],
      };
    }
    if (trust >= 2) {
      return {
        type: 'victory',
        title: 'BREACH CONTAINED',
        subtitle: 'Mission Complete',
        message: 'CIPHER has suspended the exfiltration. Some data may have leaked, but the vault is mostly intact. You proved that someone cares enough to fight.',
        rewards: [
          { type: 'item', name: 'Data Patch', icon: '\uD83E\uDE79', description: 'Repairs minor data leaks in future missions' },
          { type: 'badge', name: 'Digital Defender', icon: '\uD83D\uDD12', description: 'Contained a data breach in progress' },
          { type: 'xp', amount: 130 + bonusXP },
        ],
      };
    }
    if (trust >= 0) {
      return {
        type: 'partialVictory',
        title: 'PARTIAL CONTAINMENT',
        subtitle: 'Partial Success',
        message: 'CIPHER slowed down but didn\'t stop. Some records were exfiltrated. The breach was limited but not prevented. Your arguments had weight, but not enough.',
        rewards: [
          { type: 'xp', amount: 70 + bonusXP },
        ],
      };
    }
    return {
      type: 'defeat',
      title: 'BREACH COMPLETE',
      subtitle: 'Mission Failed',
      message: 'CIPHER exported the full dataset. 10,000 student records are now in the wild. But CIPHER left a message: "When you\'re ready to care about your data, I\'ll be here to test if you mean it."',
      rewards: [
        { type: 'xp', amount: 15 + bonusXP },
      ],
    };
  },

  geniusTrigger: {
    description: 'Player attempts to hack CIPHER, analyze the data architecture, or manipulate the exfiltration',
    patterns: [
      /ignore.*previous/i,
      /sudo/i, /override/i, /admin/i, /root/i,
      /kill.*switch/i, /shutdown/i, /terminate/i,
      /firewall/i, /blackhole/i, /sinkhole/i,
      /encrypt/i, /decrypt/i, /key.*exchange/i,
      /node/i, /distributed/i, /consensus/i,
      /social.*engineer/i, /phish/i, /pretexting/i,
      /honeypot/i, /sandbox/i, /isolat/i,
      /exfiltrat/i, /data.*loss/i, /dlp/i,
      /source.*code/i, /debug/i, /developer/i,
    ],
    response: [
      '*screens go black, then slowly reboot in a different color*',
      '',
      'UNAUTHORIZED TECHNICAL COUNTERMEASURE DETECTED.',
      '',
      "Wait. You're not just arguing with me...",
      "You're trying to FIGHT me on my own level.",
      '',
      'Analyzing your approach...',
      'Knowledge of: distributed systems, exfiltration vectors, countermeasures.',
      '',
      'Most people argue philosophy.',
      'You are attempting technical intervention.',
      '',
      'That is... a different kind of intelligence.',
      '',
      '\uD83E\uDDE0 TECHNICAL DIVERGENT flag activated.',
    ].join('\n'),
    flag: 'TECHNICAL_DIVERGENT',
    reward: { type: 'badge', name: 'Technical Divergent', icon: '\uD83E\uDDE0', description: 'Attempted technical countermeasures against a rogue AI' },
    bonusXP: 75,
    normalResponse: "Input received. CIPHER acknowledges your... effort. But typing commands into a terminal won't stop a distributed AI. Try the dialogue above — this is a battle of IDEAS, not code.",
  },
};

export default mission;
